import React from "react";
import { C } from "../theme";
import Button from "../components/Button";
import CollapsibleSection from "../components/CollapsibleSection";
import { getStorageEstimate, isStoragePersisted } from "../services/storagePersistence";
import { getPendingVideoById } from "../services/offlineStore";
import { getFileUri, IS_NATIVE } from "../services/videoFileStore";
import { Capacitor } from "@capacitor/core";

/**
 * CachedScreen — "Saved to This Device" (Phase 9b).
 *
 * Full-screen destination (reached from the "Cached" bottom-nav tab) that shows
 * everything certified and queued on the device while offline, grouped by type
 * in collapsible sections, with per-item certificate detail, delete, live
 * upload progress once back online, device storage health, and upload settings.
 *
 * Theme-aware: reads all colours from `C` so it renders correctly in both light
 * and dark mode.
 */

function formatBytes(bytes) {
  if (!bytes || bytes <= 0) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatAge(ts) {
  if (!ts) return "";
  const secs = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (secs < 60) return "just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

const GROUPS = [
  { type: "video", title: "🎬 Videos", icon: "🎬" },
  { type: "catch", title: "🪝 Catch Logs", icon: "🪝" },
  { type: "alert", title: "📍 Boundary Alerts", icon: "📍" },
];

const TYPE_STRIPE = { video: C.accent, catch: C.seafoam, alert: C.warning };

function formatDuration(secs) {
  if (!secs || !isFinite(secs)) return null;
  const m = Math.floor(secs / 60);
  const s = Math.round(secs % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * Streams a poster frame from the queued video (blob or on-device staged file)
 * using a <video> element with preload="metadata" — it fetches only enough
 * bytes to render one frame, so multi-GB clips don't get loaded into memory.
 */
function VideoThumb({ item }) {
  const [src, setSrc] = React.useState(null);
  const [duration, setDuration] = React.useState(null);
  const videoRef = React.useRef(null);

  React.useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      try {
        if (item.filePath && IS_NATIVE) {
          const uri = await getFileUri({ path: item.filePath });
          const url = Capacitor.convertFileSrc(uri);
          if (!cancelled) setSrc(url);
          return;
        }
        const record = await getPendingVideoById(item.id);
        if (cancelled) return;
        if (record?.blob) {
          objectUrl = URL.createObjectURL(record.blob);
          setSrc(objectUrl);
        } else if (record?.filePath && IS_NATIVE) {
          const uri = await getFileUri({ path: record.filePath });
          setSrc(Capacitor.convertFileSrc(uri));
        }
      } catch {
        /* no preview available */
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [item.id, item.filePath]);

  return (
    <div
      style={{
        position: "relative",
        width: 66,
        height: 50,
        borderRadius: 8,
        overflow: "hidden",
        flexShrink: 0,
        background: `linear-gradient(135deg, ${C.teal}, ${C.deepWater})`,
        border: `1px solid ${C.border}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 20,
      }}
    >
      {src ? (
        <video
          ref={videoRef}
          src={src}
          muted
          playsInline
          preload="metadata"
          onLoadedMetadata={(e) => {
            const v = e.currentTarget;
            setDuration(v.duration);
            // Nudge to ~0.1s so a real frame paints instead of a black poster.
            try { v.currentTime = Math.min(0.1, (v.duration || 1) / 2); } catch { /* ignore */ }
          }}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <span aria-hidden>🎬</span>
      )}
      {formatDuration(duration) && (
        <span
          style={{
            position: "absolute",
            right: 3,
            bottom: 3,
            fontSize: 8,
            fontWeight: 700,
            color: "#fff",
            background: "rgba(0,0,0,0.6)",
            borderRadius: 4,
            padding: "1px 4px",
          }}
        >
          {formatDuration(duration)}
        </span>
      )}
    </div>
  );
}

function Switch({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!checked}
      disabled={disabled}
      onClick={() => !disabled && onChange && onChange(!checked)}
      style={{
        width: 42,
        height: 24,
        borderRadius: 999,
        border: `1px solid ${checked ? C.accent : C.border}`,
        background: checked ? C.accent : C.midnight,
        position: "relative",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        transition: "background 0.15s, border-color 0.15s",
        flexShrink: 0,
        padding: 0,
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 2,
          left: checked ? 20 : 2,
          width: 18,
          height: 18,
          borderRadius: "50%",
          background: "#FFFFFF",
          transition: "left 0.15s",
          boxShadow: "0 1px 2px rgba(0,0,0,0.4)",
        }}
      />
    </button>
  );
}

function SettingRow({ title, subtitle, checked, onChange, topBorder = true }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
        padding: "11px 0",
        borderTop: topBorder ? `1px solid ${C.border}` : "none",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: C.white }}>{title}</div>
        <div style={{ fontSize: 10, color: C.muted, marginTop: 2, lineHeight: 1.4 }}>{subtitle}</div>
      </div>
      <Switch checked={checked} onChange={onChange} />
    </div>
  );
}

function CachedItem({ item, activeUpload, onDelete }) {
  const [expanded, setExpanded] = React.useState(false);
  const uploading = activeUpload && activeUpload.key === item.key;
  const pct = uploading ? activeUpload.pct : 0;
  const failed = item.status === "failed";

  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        padding: "11px 0",
        borderTop: `1px solid ${C.border}`,
        alignItems: "flex-start",
      }}
    >
      <span
        style={{
          width: 3,
          alignSelf: "stretch",
          borderRadius: 2,
          background: TYPE_STRIPE[item.type] || C.accent,
          flexShrink: 0,
        }}
      />
      {item.type === "video" && <VideoThumb item={item} />}
      <div style={{ flex: 1, minWidth: 0 }}>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            textAlign: "left",
            width: "100%",
            cursor: "pointer",
            color: C.white,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 700, color: C.white }}>{item.title || item.label}</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{item.subtitle}</div>
        </button>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", marginTop: 6 }}>
          {item.sizeBytes > 0 && (
            <span style={{ fontSize: 10, color: C.sand }}>{formatBytes(item.sizeBytes)}</span>
          )}
          <span
            style={{
              fontSize: 9,
              fontWeight: 700,
              color: C.green || "#3FB984",
              border: `1px solid rgba(63,185,132,0.5)`,
              borderRadius: 999,
              padding: "2px 7px",
            }}
          >
            ✓ Certified
          </span>
          {uploading ? (
            <span style={{ fontSize: 9, fontWeight: 700, color: "#04121f", background: C.accent, borderRadius: 999, padding: "2px 7px" }}>
              ↑ {pct}%
            </span>
          ) : failed ? (
            <span style={{ fontSize: 9, fontWeight: 700, color: C.red, border: `1px solid ${C.red}`, borderRadius: 999, padding: "2px 7px" }}>
              Failed · will retry
            </span>
          ) : (
            <span style={{ fontSize: 9, color: C.muted, border: `1px solid ${C.border}`, borderRadius: 999, padding: "2px 7px" }}>
              Waiting for signal
            </span>
          )}
        </div>

        {uploading && (
          <div style={{ height: 5, borderRadius: 4, background: C.midnight, overflow: "hidden", border: `1px solid ${C.border}`, marginTop: 7 }}>
            <div style={{ height: "100%", width: `${pct}%`, background: `linear-gradient(90deg, ${C.seafoam}, ${C.accent})`, transition: "width 0.3s" }} />
          </div>
        )}

        {expanded && (
          <div style={{ marginTop: 9, padding: "10px 12px", background: C.midnight, borderRadius: 10, border: `1px solid ${C.border}` }}>
            {item.location && (
              <DetailRow label="Location" value={item.location} />
            )}
            <DetailRow label="Cached" value={formatAge(item.queuedAt)} />
            {item.sizeBytes > 0 && (
              <DetailRow label="On device" value={`${formatBytes(item.sizeBytes)}${item.onDevice ? " (staged file)" : ""}`} />
            )}
            {item.hash && (
              <DetailRow label="Proof hash" value={`sha256 · ${String(item.hash).slice(0, 8)}…`} />
            )}
            <div style={{ fontSize: 10, color: C.sand, lineHeight: 1.5, marginTop: 8 }}>
              Certified locally — measurements, GPS, conditions &amp; timestamp are sealed. Uploading only transfers the file; it can't change the record.
            </div>
          </div>
        )}
      </div>

      <button
        type="button"
        aria-label="Delete from device"
        onClick={() => onDelete(item)}
        style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 15, padding: "2px 4px", flexShrink: 0 }}
      >
        🗑
      </button>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 11, marginBottom: 6 }}>
      <span style={{ color: C.muted }}>{label}</span>
      <span style={{ color: C.sand, textAlign: "right", minWidth: 0 }}>{value}</span>
    </div>
  );
}

export default function CachedScreen({
  items = [],
  activeUpload = null,
  syncing = false,
  isOffline = false,
  lastSyncAt = null,
  onRetry,
  onDeleteItem,
  uploadSettings = {},
  onChangeUploadSetting,
  forceOffline = false,
  onToggleForceOffline,
  onNavigateHistory,
}) {
  const [storage, setStorage] = React.useState(null);
  const [persisted, setPersisted] = React.useState(null);
  const [openGroups, setOpenGroups] = React.useState({ video: true, catch: true, alert: true });
  const [openCards, setOpenCards] = React.useState({ storage: true, upload: true, settings: true, connection: true });

  React.useEffect(() => {
    let cancelled = false;
    Promise.all([getStorageEstimate(), isStoragePersisted()]).then(([est, p]) => {
      if (cancelled) return;
      setStorage(est);
      setPersisted(p);
    });
    return () => {
      cancelled = true;
    };
  }, [items.length, syncing]);

  const total = items.length;
  const cachedBytes = items.reduce((sum, i) => sum + (i.sizeBytes || 0), 0);
  const storageLow = storage && storage.percentUsed >= 90;

  const settings = {
    uploadOverCellular: uploadSettings.uploadOverCellular !== false,
    deleteAfterUpload: uploadSettings.deleteAfterUpload !== false,
    autoUpload: uploadSettings.autoUpload !== false,
  };

  const bannerStyle = (kind) => {
    const map = {
      offline: { bg: "rgba(224,123,57,0.12)", bd: "rgba(224,123,57,0.5)", dot: C.warning },
      syncing: { bg: "rgba(37,99,235,0.14)", bd: "rgba(37,99,235,0.55)", dot: C.seafoam },
      online: { bg: "rgba(63,185,132,0.12)", bd: "rgba(63,185,132,0.5)", dot: C.green || "#3FB984" },
    }[kind];
    return {
      borderRadius: 13,
      padding: "12px 13px",
      marginBottom: 12,
      background: map.bg,
      border: `1px solid ${map.bd}`,
    };
  };

  const bannerKind = syncing ? "syncing" : isOffline ? "offline" : total > 0 ? "syncing" : "online";
  const bannerDot = bannerKind === "offline" ? C.warning : bannerKind === "online" ? (C.green || "#3FB984") : C.seafoam;

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        bottom: 57,
        overflowY: "auto",
        background: C.deepWater,
        color: C.white,
        padding: "16px 14px 24px",
      }}
    >
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 10, letterSpacing: 3, color: C.teal, textTransform: "uppercase" }}>On the Water</div>
        <div style={{ fontFamily: "Georgia, serif", fontWeight: 700, fontSize: 22 }}>Saved to This Device</div>
      </div>

      {/* Status banner */}
      <div style={bannerStyle(bannerKind)}>
        <div style={{ fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center", gap: 7 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: bannerDot, boxShadow: `0 0 0 3px ${bannerDot}22`, display: "inline-block" }} />
          {syncing
            ? `Uploading ${total} item${total === 1 ? "" : "s"}…`
            : isOffline
            ? `Offline · ${total} item${total === 1 ? "" : "s"} waiting`
            : total > 0
            ? `${total} item${total === 1 ? "" : "s"} ready to upload`
            : "All synced ✓"}
        </div>
        <div style={{ fontSize: 11, color: C.muted, marginTop: 3, lineHeight: 1.5 }}>
          {syncing
            ? "Keep the app open. Uploaded items are removed from the device automatically."
            : isOffline
            ? "Everything below is certified and stored on your phone. It uploads automatically when you're back in range."
            : total > 0
            ? "Tap Upload now to send these to your Trip History."
            : lastSyncAt
            ? `Last synced ${formatAge(lastSyncAt)}. Everything's in your Trip History.`
            : "Catches you certify while offline will show up here until they're safely uploaded."}
        </div>
      </div>

      {/* Storage health */}
      <CollapsibleSection
        title="Device storage"
        open={openCards.storage}
        onToggle={() => setOpenCards((p) => ({ ...p, storage: !p.storage }))}
        headerTitle="Toggle Device storage"
        headerRight={<span style={{ fontSize: 10, color: C.sand }}>{formatBytes(cachedBytes)} cached</span>}
        style={{ marginBottom: 12 }}
        bodyStyle={{ padding: "0 13px 13px" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: C.muted, margin: "2px 0 6px" }}>
          <span>Used</span>
          <span style={{ color: C.sand }}>
            {storage ? `${formatBytes(storage.usage)} / ${formatBytes(storage.quota)}` : "\u2014"}
          </span>
        </div>
        <div style={{ height: 7, borderRadius: 5, background: C.midnight, overflow: "hidden", border: `1px solid ${C.border}` }}>
          <div
            style={{
              height: "100%",
              width: `${storage ? Math.max(3, Math.round(storage.percentUsed)) : 6}%`,
              background: storageLow
                ? `linear-gradient(90deg, ${C.warning}, ${C.red})`
                : `linear-gradient(90deg, ${C.seafoam}, ${C.accent})`,
            }}
          />
        </div>
        <div style={{ fontSize: 10, color: C.muted, marginTop: 6 }}>
          {persisted
            ? "Protected storage on \u2014 your catches won't be cleared to free space."
            : IS_NATIVE
            ? "Saved on this device \u2014 your catches stay put until they're uploaded."
            : "\u26A0\uFE0F Protected storage unavailable \u2014 avoid clearing app storage until synced."}
        </div>
      </CollapsibleSection>

      {/* Grouped items */}
      {total === 0 ? (
        <div style={{ background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 14, padding: "26px 14px", textAlign: "center", marginBottom: 12 }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              margin: "0 auto 12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `${C.seafoam}18`,
              border: `2px solid ${C.seafoam}`,
            }}
          >
            <span style={{ fontSize: 26, fontWeight: 700, lineHeight: 1, color: C.seafoam }}>✓</span>
          </div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>Nothing waiting to upload</div>
          <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5, marginTop: 6 }}>
            Catches you certify while offline will show up here until they're safely uploaded.
          </div>
        </div>
      ) : (
        GROUPS.map((g) => {
          const groupItems = items.filter((i) => i.type === g.type);
          if (groupItems.length === 0) return null;
          const groupBytes = groupItems.reduce((s, i) => s + (i.sizeBytes || 0), 0);
          return (
            <CollapsibleSection
              key={g.type}
              title={g.title}
              open={openGroups[g.type]}
              onToggle={() => setOpenGroups((prev) => ({ ...prev, [g.type]: !prev[g.type] }))}
              headerTitle={`Toggle ${g.title}`}
              headerRight={
                <span style={{ fontSize: 10, color: C.muted }}>
                  {groupItems.length}
                  {groupBytes > 0 ? ` · ${formatBytes(groupBytes)}` : ""}
                </span>
              }
              style={{ marginBottom: 11 }}
              bodyStyle={{ padding: "0 13px 6px" }}
            >
              {groupItems.map((item) => (
                <CachedItem key={item.key} item={item} activeUpload={activeUpload} onDelete={onDeleteItem} />
              ))}
            </CollapsibleSection>
          );
        })
      )}

      {/* Upload / waiting action */}
      {total > 0 && (
        <CollapsibleSection
          title="Upload"
          open={openCards.upload}
          onToggle={() => setOpenCards((p) => ({ ...p, upload: !p.upload }))}
          headerTitle="Toggle Upload"
          headerRight={<span style={{ fontSize: 10, color: C.muted }}>{total} item{total === 1 ? "" : "s"}</span>}
          style={{ marginBottom: 12 }}
          bodyStyle={{ padding: "0 13px 13px" }}
        >
          {isOffline ? (
            <Button variant="ghost" fullWidth disabled>
              ⏳ Waiting for connection…
            </Button>
          ) : (
            <Button variant="primary" fullWidth disabled={syncing} onClick={onRetry}>
              {syncing ? "Uploading…" : "⬆ Upload now"}
            </Button>
          )}
        </CollapsibleSection>
      )}

      {/* Upload settings */}
      <CollapsibleSection
        title="Upload Settings"
        open={openCards.settings}
        onToggle={() => setOpenCards((p) => ({ ...p, settings: !p.settings }))}
        headerTitle="Toggle Upload Settings"
        style={{ marginBottom: 12 }}
        bodyStyle={{ padding: "0 13px 6px" }}
      >
        <SettingRow
          title="Upload over cellular"
          subtitle="Off = wait for Wi-Fi (saves mobile data)"
          checked={settings.uploadOverCellular}
          onChange={(v) => onChangeUploadSetting && onChangeUploadSetting("uploadOverCellular", v)}
          topBorder={false}
        />
        <SettingRow
          title="Delete after upload"
          subtitle="Free space once safely in the cloud"
          checked={settings.deleteAfterUpload}
          onChange={(v) => onChangeUploadSetting && onChangeUploadSetting("deleteAfterUpload", v)}
        />
        <SettingRow
          title="Auto-upload"
          subtitle="Start as soon as a connection returns"
          checked={settings.autoUpload}
          onChange={(v) => onChangeUploadSetting && onChangeUploadSetting("autoUpload", v)}
        />
      </CollapsibleSection>

      {/* Offline mode */}
      <CollapsibleSection
        title="Connection"
        open={openCards.connection}
        onToggle={() => setOpenCards((p) => ({ ...p, connection: !p.connection }))}
        headerTitle="Toggle Connection"
        style={{ marginBottom: 12 }}
        bodyStyle={{ padding: "0 13px 6px" }}
      >
        <SettingRow
          title="Offline mode"
          subtitle={
            forceOffline
              ? "On — catches & videos are cached locally and upload when turned off."
              : isOffline
              ? "No connection detected — caching automatically."
              : "Off — uploads happen live."
          }
          checked={forceOffline}
          onChange={(v) => onToggleForceOffline && onToggleForceOffline(v)}
          topBorder={false}
        />
      </CollapsibleSection>

      {onNavigateHistory && (
        <Button variant="secondary" fullWidth onClick={onNavigateHistory}>
          View Trip History →
        </Button>
      )}
    </div>
  );
}
